import { Card, Col, DatePicker, Row, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import useAxiosPost from "../hooks/useAxiosPost";
import useMultiNotification from "../hooks/useNotification";
import TailSpinLoader from "../components/Items/Loaders/TailSpinLoader";
import NetwalaSelect from "../components/Items/Select/Index";
import "./TotalCollectedAmountPage.css";

const { RangePicker } = DatePicker;

const COLLECTION_TYPE_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Recharged total amount", value: "recharge" },
  { label: "Inventory sale amount", value: "inventory_sale" },
  { label: "Other services total amount", value: "other_services" },
];

const TotalCollectedAmountPage = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(() => [
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [collectionType, setCollectionType] = useState<string>("all");

  const [
    getTotalCollectedAmount,
    response,
    error,
    loading,
  ] = useAxiosPost<ResponseProp>("reports/getTotalCollectedAmount");

  const [totalCollected, setTotalCollected] = useState<number | null>(null);

  useEffect(() => {
    if (dateRange?.length !== 2) return;
    getTotalCollectedAmount({
      startDate: dateRange[0].format("YYYY-MM-DD"),
      endDate: dateRange[1].format("YYYY-MM-DD"),
      collectionType: collectionType === "all" ? undefined : collectionType,
    });
  }, [dateRange, collectionType]);

  useEffect(() => {
    const data = response?.responseData as Record<string, unknown> | undefined;
    if (data != null && typeof data.totalCollected === "number") {
      setTotalCollected(data.totalCollected as number);
    }
  }, [response]);

  useEffect(() => {
    if (error) openNotification("error", "Error", error);
  }, [error]);

  const typeLabel =
    COLLECTION_TYPE_OPTIONS.find((o) => o.value === collectionType)?.label ?? "All";

  return (
    <>
      {contextHolder}
      <div className="total-collected-amount-page">
        <Typography.Title level={4} className="total-collected-page-title">
          Total Collected Amount
        </Typography.Title>

        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <div className="total-collected-filter-group">
              <span className="total-collected-filter-label">Date range</span>
              <RangePicker
                value={dateRange}
                onChange={(dates) =>
                  setDateRange(
                    dates && dates[0] && dates[1] ? [dates[0], dates[1]] : null
                  )
                }
                format="DD MMM YYYY"
                className="total-collected-date-picker"
                style={{ width: "100%" }}
              />
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div className="total-collected-filter-group">
              <span className="total-collected-filter-label">Collection type</span>
              <NetwalaSelect
                value={collectionType}
                onChange={(value) => setCollectionType(value as string)}
                options={COLLECTION_TYPE_OPTIONS}
                size="large"
                height="40px"
              />
            </div>
          </Col>
        </Row>

        {loading ? (
          <div className="total-collected-loading">
            <TailSpinLoader />
          </div>
        ) : (
          <Card className="total-collected-card">
            <div className="total-collected-result">
              <Typography.Text className="total-collected-label">
                {typeLabel} ({" "}
                {dateRange?.length === 2
                  ? `${dateRange[0].format("DD MMM YYYY")} – ${dateRange[1].format("DD MMM YYYY")}`
                  : "—"}
                )
              </Typography.Text>
              <Typography.Text className="total-collected-value">
                {totalCollected != null
                  ? Number(totalCollected).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : "—"}
              </Typography.Text>
            </div>
          </Card>
        )}
      </div>
    </>
  );
};

export default TotalCollectedAmountPage;
